import {
  itemInActiveWindow,
  itemIsActive,
  pickFirstText,
  statusIsRuntimeActive,
} from './formatters'

function ruleIsRuntimeActive(rule) {
  return itemInActiveWindow(rule)
}

function filterText(filters, ...keys) {
  return pickFirstText(...keys.map((key) => filters?.[key]))
}

export function ruleTargetsItem(rule, item, kind) {
  if (!ruleIsRuntimeActive(rule)) return false
  const filters = rule.filter_logic || {}
  if (item.market && rule.market && item.market !== rule.market) return false
  if (kind === 'promotion') {
    if (rule.rule_type !== 'promo_boost') return false
    const promoType = filterText(filters, 'promo_type', 'promo_types')
    return !promoType || promoType === item.promo_type
  }
  if (kind === 'jackpot') {
    if (rule.rule_type !== 'jackpot_rule') return false
    const jackpotType = filterText(filters, 'jackpot_type', 'jackpot_types')
    return !jackpotType || jackpotType === item.jackpot_type
  }
  return false
}

export function runtimeStatusLabel(item, kind) {
  if (kind === 'metadata') {
    const status = String(item.status || '').toLowerCase()
    return itemIsActive(item) && ['approved', 'overridden'].includes(status) ? 'Runtime active' : 'Review only'
  }
  return itemInActiveWindow(item) && statusIsRuntimeActive(item.status) ? 'Runtime active' : 'Not active in runtime'
}

export function buildPromotionImpact(item, rules, { rulesReady = true } = {}) {
  const gameCodes = Array.isArray(item.eligible_game_ids) ? item.eligible_game_ids : []
  const matchingRules = rules.filter((rule) => ruleTargetsItem(rule, item, 'promotion'))
  const warnings = []
  if (!gameCodes.length) warnings.push('Add eligible game IDs before this can affect search.')
  if (rulesReady && runtimeStatusLabel(item, 'promotion') === 'Runtime active' && !matchingRules.length) {
    warnings.push('No active promotion boost or row rule uses this promotion yet.')
  }
  return {
    status: runtimeStatusLabel(item, 'promotion'),
    count: gameCodes.length,
    topGames: gameCodes.slice(0, 5),
    rules: matchingRules.map((rule) => rule.label),
    warnings,
  }
}

export function buildJackpotImpact(item, rules, { rulesReady = true } = {}) {
  const gameCodes = Array.isArray(item.participating_game_ids) ? item.participating_game_ids : []
  const matchingRules = rules.filter((rule) => ruleTargetsItem(rule, item, 'jackpot'))
  const warnings = []
  if (!gameCodes.length) warnings.push('Add participating game IDs before this can affect search.')
  if (item.jackpot_type === 'must_drop' && !item.must_drop_at) {
    warnings.push('Must-drop labels require a real deadline.')
  }
  if (rulesReady && runtimeStatusLabel(item, 'jackpot') === 'Runtime active' && !matchingRules.length) {
    warnings.push('No active jackpot rule uses this pool yet.')
  }
  return {
    status: runtimeStatusLabel(item, 'jackpot'),
    count: gameCodes.length,
    topGames: gameCodes.slice(0, 5),
    rules: matchingRules.map((rule) => rule.label),
    warnings,
  }
}

export function buildMetadataImpact(item, runtime) {
  const status = runtimeStatusLabel(item, 'metadata')
  const runtimeOverrides = runtime?.metadata_overrides || []
  const included = runtimeOverrides.some((override) => override.game_code === item.game_code)
  const warnings = []
  if (status === 'Review only') {
    warnings.push('Only approved or overridden records affect the player-facing runtime.')
  } else if (!included && runtime) {
    warnings.push('This record is saved but is not present in the current runtime payload yet.')
  }
  return {
    status,
    count: status === 'Runtime active' ? 1 : 0,
    topGames: item.game_code ? [item.game_code] : [],
    rules: included ? ['Runtime metadata override'] : [],
    warnings,
  }
}
